// @ts-strict-ignore
import { ArrayUtils } from '@shared/util/array-utils';
import { StringUtils } from '@shared/util/string-utils';
import { LoginInfo } from '@user/model/login-info';
import { ArticleCollectionVO } from '@article/model/vo/article.vo';
import { ReportGenerationPropertyVO, UserSettingPropertyVO } from '@project/components/article/model/vo/property.vo';
import { ArticleCollectionUtils } from '@article/util/article-collection-utils';
import { JsonObject, JsonProperty } from '@shared/convert/convert-core';
import { UserPermissionName } from '../../constants/user-permissions';

export const userRoleNames = [
  'ADMIN',
  'CUSTOMER',
  'PRODUCTION',
  'CLERK',
  'SALES',
  'GUEST',
  'API',
];

export type UserRoleName = typeof userRoleNames[number];

@JsonObject('UserRoleVO')
export class UserRoleVO {

  @JsonProperty('name', 'String')
  public name: UserRoleName = undefined;
}

@JsonObject('UserPermissionVO')
export class UserPermissionVO {
  @JsonProperty('name', 'String')
  public name: UserPermissionName = undefined;
}

@JsonObject('OrganizationVO')
export class OrganizationVO {
  @JsonProperty('id', 'Number')
  public id: number = NaN;

  @JsonProperty('name', 'String')
  public name: string = null;

  @JsonProperty('description', 'String')
  public description: string = null;
}

@JsonObject('CountryInfoVO')
export class CountryInfoVO {
  @JsonProperty('iso2', 'String')
  public iso2: string = undefined;

  @JsonProperty('iso3', 'String')
  public iso3: string = undefined;

  @JsonProperty('isoNr', 'Number')
  public isoNr: number = undefined;

  @JsonProperty('name', 'String')
  public name: string = undefined;

  @JsonProperty('officialName', 'String')
  public officialName: string = undefined;

  @JsonProperty('currencyIso3', 'String')
  public currencyIso3: string = undefined;

  @JsonProperty('currencyIsoNr', 'Number')
  public currencyIsoNr: number = undefined;

  @JsonProperty('defaultProductionCountry', 'String')
  public defaultProductionCountry: string = undefined;
}

@JsonObject('AddressVO')
export class AddressVO {
  @JsonProperty('street', 'String')
  public street: string = undefined;

  @JsonProperty('zipCode', 'String')
  public zipCode: string = undefined;

  @JsonProperty('city', 'String')
  public city: string = undefined;

  @JsonProperty('country', 'CountryInfoVO')
  public country: CountryInfoVO = undefined;

  @JsonProperty('phone', 'String')
  public phone: string = undefined;

  @JsonProperty('fax', 'String')
  public fax: string = undefined;
}

@JsonObject('UserVO')
export class UserVO {
  public static readonly DEFAULT_GUEST_ACCOUNT_ID: number = 0;
  public static readonly DEFAULT_GUEST_ACCOUNT_LOGIN_INFO: LoginInfo = new LoginInfo('guest', 'guest', null);

  public static readonly ANONYMOUS: UserVO = UserVO.createAnonymousUserVO();

  @JsonProperty('id', 'Number')
  public id: number = NaN;

  @JsonProperty('login', 'String')
  public login: string = null;

  @JsonProperty('email', 'String')
  public email: string = null;

  @JsonProperty('gender', 'String')
  public gender: string = null;

  @JsonProperty('name', 'String')
  public name: string = null;

  @JsonProperty('surname', 'String')
  public surname: string = null;

  @JsonProperty('roles', '[UserRoleVO]')
  public roles: UserRoleVO[] = new Array<UserRoleVO>();

  @JsonProperty('permissions', '[UserPermissionVO]')
  public permissions: UserPermissionVO[] = new Array<UserPermissionVO>();

  @JsonProperty('salutation', 'String')
  public salutation: string = null;

  @JsonProperty('department', 'String')
  public department: string = null;

  @JsonProperty('functionalRole', 'String')
  public functionalRole: string = null;

  @JsonProperty('organization', 'OrganizationVO')
  public organization: OrganizationVO = null;

  @JsonProperty('address', 'AddressVO')
  public address: AddressVO = null;

  @JsonProperty('addressNumber', 'String')
  public addressNumber: string = null;

  @JsonProperty('salesOrganisation', 'String')
  public salesOrganisation: string = null;

  @JsonProperty('cdcUserRole', 'UserRoleVO')
  public cdcUserRole: UserRoleVO = null;

  @JsonProperty('deleted', "Boolean")
  public deleted: boolean = false;

  @JsonProperty('mdmGroup', 'MdmGroupVO')
  public mdmGroup: MdmGroupVO = null;

  private static createAnonymousUserVO(): UserVO {
    const userVO: UserVO = new UserVO();
    userVO.id = NaN;
    userVO.login = '';
    return userVO;
  }

  public getDisplayName(): string {
    if (this.name && this.surname) {
      return this.name + ' ' + this.surname;
    } else if (this.name && !this.surname) {
      return this.name;
    } else if (!this.name && this.surname) {
      return this.surname;
    } else {
      return '';
    }
  }

  public isRegisteredUserAccount(): boolean {
    return this.id !== UserVO.DEFAULT_GUEST_ACCOUNT_ID && this !== UserVO.ANONYMOUS;
  }

  public isAdmin(): boolean {
    return this.hasRole('ADMIN');
  }

  public isClerk(): boolean {
    return this.hasRole('CLERK');
  }

  public isCustomer(): boolean {
    return this.hasRole('CUSTOMER');
  }

  public isProduction(): boolean {
    return this.hasRole('PRODUCTION');
  }

  public isSales(): boolean {
    return this.hasRole('SALES');
  }

  public isWago(): boolean {
    return this.isAdmin() || this.isClerk() || this.isSales();
  }

  private hasRole(roleName: string): boolean {
    if (ArrayUtils.isEmpty(this.roles)) {
      return false;
    }
    for (let i: number = 0; i < this.roles.length; i++) {
      const role: UserRoleVO = this.roles[i];
      if (role.name === roleName) {
        return true;
      }
    }
    return false;
  }

  public hasPermission(permissionName: UserPermissionName): boolean {
    if (ArrayUtils.isEmpty(this.permissions) || StringUtils.isEmpty(permissionName)) {
      return false;
    }
    for (let i: number = 0; i < this.permissions.length; i++) {
      const permission: UserPermissionVO = this.permissions[i];
      if (permission.name === permissionName) {
        return true;
      }
    }
    return false;
  }

  public hasPermissions(permissionNames: UserPermissionName[], grantUnregisteredUserAccountsAccess = false): boolean {
    if (grantUnregisteredUserAccountsAccess && !this.isRegisteredUserAccount()) {
      return true;
    }
    if (ArrayUtils.isEmpty(this.permissions)) {
      return ArrayUtils.isEmpty(permissionNames);
    }
    return permissionNames.every((permissionName: UserPermissionName) => this.hasPermission(permissionName));
  }
}

@JsonObject('UserProfileVO')
export class UserProfileVO {
  public static readonly ANONYMOUS: UserProfileVO = UserProfileVO.createAnonymousUserProfileVO();

  @JsonProperty('user', 'UserVO')
  public user: UserVO = undefined;

  @JsonProperty('articleCollections', '[ArticleCollectionVO]')
  public articleCollections: ArticleCollectionVO[] = undefined;

  @JsonProperty('reportGenerationProperties', '[ReportGenerationPropertyVO]')
  public reportGenerationProperties: ReportGenerationPropertyVO[] = undefined;

  @JsonProperty('userSettingProperties', '[UserSettingPropertyVO]')
  public userSettingProperties: UserSettingPropertyVO[] = undefined;

  private static createAnonymousUserProfileVO(): UserProfileVO {
    const userProfileVO: UserProfileVO = new UserProfileVO();
    userProfileVO.user = UserVO.ANONYMOUS;
    const articleCollection: ArticleCollectionVO = ArticleCollectionUtils.createArticleCollection(ArticleCollectionVO.TYPE_FAVORITES);
    userProfileVO.articleCollections = [articleCollection];
    return userProfileVO;
  }

  public isRegisteredUserAccount(): boolean {
    if (!this.user) {
      return false;
    }
    return this.user.isRegisteredUserAccount();
  }
}

@JsonObject('MdmGroupVO')
export class MdmGroupVO {
  @JsonProperty('id', 'number')
  public id: number = undefined;

  @JsonProperty('name', 'string')
  public name: string = undefined;

  public static areEqual(mdmGroup1: MdmGroupVO, mdmGroup2: MdmGroupVO): boolean {
    if (mdmGroup1?.id == null || mdmGroup2?.id == null) return false;
    if (mdmGroup1 == mdmGroup2) return true;
    return mdmGroup1.id === mdmGroup2.id;
  }
}
